import { createFileRoute } from '@tanstack/react-router';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Walking Learning: Boost Academic Performance by 60%';
const heroDescription =
  'Turn your daily walks into productive learning sessions. Listen to academic papers, research, and study materials while getting exercise and fresh air.';

const featureTitle = heroTitle;
const featureSubtitle = 'Walk and learn simultaneously';
const featureDescription = heroDescription;
const featureBadge = 'Healthy Learning';
const featureVariant = 'default';
const featureCtaText = 'Start Walking & Learning';

const featureFeatures = [
  {
    title: 'Perfect for Exercise',
    description:
      'Combine physical activity with mental stimulation for a healthier, more productive routine.',
  },
  {
    title: 'Hands-Free Learning',
    description:
      'No need to carry books or look at screens - just listen and absorb information naturally.',
  },
  {
    title: 'Fresh Air Focus',
    description:
      'Studies show that walking outdoors can improve concentration and information retention.',
  },
];

const seoSections = [
  {
    title: 'Why Walking Boosts Learning Performance by 60%',
    content:
      "Stanford research reveals that walking increases creative output by an average of 60% compared to sitting, with the benefits applying specifically to divergent thinking and idea generation - exactly what students and researchers need for comprehending complex academic material. This isn't just casual correlation: studies using EEG brain monitoring show that outdoor walking produces measurable increases in P300 brain activity, the neural response associated with attention and working memory that's crucial for academic learning.",
    contentSecondary:
      "Unlike traditional multitasking which impairs performance and increases errors, walking-based learning works because walking is an automated task that doesn't compete for cognitive resources. Research confirms that students demonstrate significantly higher learning retention when walking and listening to educational content versus sitting, plus improved performance on cognitive tests administered post-walk. The walking advantage extends beyond retention to mood enhancement, with studies showing positive affect increases during walking-learning sessions while sitting-learning produces negative emotional responses.",
  },
  {
    title: 'Transform Dead Time Into Academic Advancement',
    content:
      "Most students and researchers waste 1-2 hours daily during commutes, exercise, and walks that could become productive academic learning time. Walking while consuming academic content isn't just convenient - it's scientifically superior to traditional desk-based studying for comprehension and creative thinking. The combination enhances working memory, focus, and both convergent and divergent creativity compared to stationary learning, making it perfect for processing research papers, academic lectures, and complex theoretical content.",
    contentSecondary:
      'This approach solves the modern productivity crisis where traditional multitasking fails students. Research shows that attempting multiple cognitive tasks simultaneously leads to lower GPAs and increased errors, but walking-learning avoids these costs because walking is automated movement that enhances rather than competes with mental processing. Users consistently report that combining daily walks with academic content consumption allows them to stay current with literature, process research papers, and advance their studies without sacrificing exercise or personal time.',
  },
];

const solutionBenefitsData = {
  title: 'Triple Your Research Productivity',
  items: [
    {
      text: 'Researchers transform 60 minutes of daily walking into academic advancement',
      highlight: 'Researchers',
    },
    {
      text: 'Students process 3x more research papers in same time through walking',
      highlight: 'Students',
    },
    {
      text: 'Academic institutions maintain fitness while advancing academic career goals',
      highlight: 'Academic institutions',
    },
    {
      text: 'Independent scholars enhance cognitive skills and academic performance',
      highlight: 'Independent scholars',
    },
  ],
};

export const Route = createFileRoute('/_marketing/_seo/listen-while-walking')({
  head: () => ({
    meta: [
      ...seo({
        title:
          'Walking Learning: Turn Walks Into Academic Study Sessions | Listening',
        description:
          'Turn walks into productive learning sessions with academic content. Science shows 60% creativity boost while walking. Perfect for students & researchers.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
    />
  ),
});
